import React, { useState } from 'react';
import type { HilPresentationAction } from '@bizfirst/hil-types';
import { HilActionBar } from '../actions/HilActionBar';
import { useHilFormRenderer } from '../context/HilFormRendererContext';

interface FormStepRendererProps {
  formID: number | null;
  formSchemaJson: string | null;
  prePopulate: Record<string, unknown>;
  promptMessage: string | null;
  actions: HilPresentationAction[];
  disabled?: boolean;
  onAction: (action: HilPresentationAction, formData: Record<string, unknown>) => void;
}

export function FormStepRenderer({
  formID,
  formSchemaJson,
  prePopulate,
  promptMessage,
  actions,
  disabled,
  onAction,
}: FormStepRendererProps): React.JSX.Element {
  const [formData, setFormData] = useState<Record<string, unknown>>(prePopulate ?? {});
  const [rawFormData, setRawFormData] = useState<string>(() => JSON.stringify(prePopulate ?? {}, null, 2));
  const formRenderer = useHilFormRenderer();

  const handleAction = (action: HilPresentationAction) => {
    onAction(action, formData);
  };

  return (
    <div className="hil-step-content">
      {promptMessage && <p className="hil-prompt">{promptMessage}</p>}
      {formID && formRenderer ? (
        formRenderer({
          formID,
          prePopulate: prePopulate ?? {},
          onValuesChange: setFormData,
          disabled,
        })
      ) : formID ? (
        <div className="hil-info-block">
          <p style={{ color: 'var(--hil-text-secondary)', fontSize: '13px' }}>
            Atlas Form #{formID} — integrate with @atlas-forms/player-components-react
          </p>
          {Object.keys(prePopulate).length > 0 && (
            <p style={{ fontSize: '12px', color: 'var(--hil-text-muted)' }}>
              Pre-populated: {Object.keys(prePopulate).join(', ')}
            </p>
          )}
        </div>
      ) : formSchemaJson ? (
        <div className="hil-info-block">
          <p style={{ fontSize: '13px', color: 'var(--hil-text-secondary)' }}>
            Inline schema form — render with your form engine
          </p>
        </div>
      ) : (
        <div>
          <label className="hil-label" htmlFor="hil-form-data">Form Data</label>
          <textarea
            id="hil-form-data"
            className="hil-textarea"
            style={{ minHeight: 120 }}
            value={rawFormData}
            onChange={(e) => {
              const text = e.target.value;
              setRawFormData(text);
              try { setFormData(JSON.parse(text)); } catch {}
            }}
            disabled={disabled}
          />
        </div>
      )}
      <HilActionBar actions={actions} onAction={handleAction} disabled={disabled} />
    </div>
  );
}
